import {
    Ecommerce as ListingsEcommerce,
    Taxonomy as OnlyListingsTaxonomy
} from './listings';

import { Taxonomy as OnlyListingTaxonomy } from './listing';
import { Ecommerce as LeadEcommerce } from './lead';
import { Taxonomy as OnlyContactTaxonomy } from './contact';
import {
    Taxonomy as OnlyPropertyTaxonomy,
    Ecommerce as PropertyEcommerce
} from './property';

import {
    Taxonomy as OnlyAgentsTaxonomy,
    AgentsEcommerce,
    AgentEcommerce
} from './agents';
import { EmailAllResultEcommerce } from '../../graphql.generated';

export type TrackingContract = {
    baselayer: Baselayer;
    taxonomy?: Taxonomy | Record<string, unknown>;
    ecommerce?: Ecommerce;
};

export type TaxonomyPage =
    | '/homepage/'
    | '/home-values/'
    | '/valuation/'
    | '/privacy/'
    | '/discover/'
    | '/property/details/'
    | '/for-sale/results/'
    | '/for-sale/details/'
    | '/for-sale/contact/'
    | '/to-rent/results/'
    | '/to-rent/details/'
    | '/to-rent/contact/'
    | '/new-homes/results/'
    | '/new-homes/details/'
    | '/new-homes/contact/'
    | '/signin/'
    | '/register/'
    | '/schemes/help-to-buy/'
    | '/schemes/help-to-buy/scotland/'
    | '/schemes/help-to-buy/wales/'
    | '/schemes/shared-ownership/'
    | '/agent-portfolio/'
    | '/house-prices/'
    | '/house-prices/results/'
    | '/my-home/select/'
    | '/myaccount/my-home/'
    | '/myaccount/my-home/local-activity/'
    | '/myaccount/my-home/estimate/'
    | '/myaccount/my-home/local-market-stats/'
    | '/myaccount/my-situation/'
    | '/property/uprn/'
    | '/find-agents/estate-agents/results/'
    | '/find-agents/letting-agents/results/'
    | '/find-agents/results/'
    | '/agent-matcher/';

export type TaxonomySection =
    | 'homepage'
    | 'home-values'
    | 'house-prices'
    | 'for-sale'
    | 'to-rent'
    | 'new-homes'
    | 'privacy'
    | 'discover'
    | 'signin'
    | 'register'
    | 'help-to-buy'
    | 'shared-ownership'
    | 'new homes/developers'
    | 'agent-portfolio'
    | 'my-home'
    | 'myaccount'
    | 'find-agents'
    | 'agent-matcher';

export type BaseTaxonomy = {
    section: TaxonomySection;
    page: TaxonomyPage;
    pagePosition?: 'top';
    countryCode?:
        | 'gb'
        | 'es'
        | 'fr'
        | 'us'
        | 'pt'
        | 'it'
        | 'ie'
        | 'gr'
        | 'de'
        | 'za'
        | 'ch'
        | 'th'
        | 'tr'
        | 'cy'
        | 'ae';
    signedInStatus?: 'signed in' | 'signed out';
    brand: string;
    brandPrimary?: string;
    os?: string;
    platform?: 'desktop' | 'responsive' | 'mobile' | 'React';
    path?: string;
    listingsCategory?: 'residential' | 'development' | '';
    activity?:
        | 'house_prices_search'
        | 'listing_search'
        | 'listing_details'
        | 'listing_contact'
        | 'editorial'
        | 'development details page'
        | 'agent_developer_directory'
        | 'email_lead_conversion'
        | 'my_home_landing'
        | 'my_home_hub'
        | 'property_details'
        | 'my_home_estimate'
        | 'my_home_notifications'
        | 'agent_matcher';
    aboutMeStatus?: string;
    device?: string;
    editorialFormat?: string;
    editorialTopic?: string[];
    sharePlatform?: 'facebook' | 'twitter' | 'copy link';
    userId?: string;
    branch_id?: number;
};

export type Baselayer = {
    session_id?: string;
    instance_id?: string;
    device_id?: string;
    session_count?: number;
    event_id?: string;
};

export type Ecommerce =
    | ListingsEcommerce
    | LeadEcommerce
    | PropertyEcommerce
    | AgentsEcommerce
    | AgentEcommerce
    | EmailAllResultEcommerce;

export type {
    AgentEcommerce,
    AgentsEcommerce,
    Agent as AgentData
} from './agents';

export type Taxonomy =
    | BaseTaxonomy
    | ListingsTaxonomy
    | ListingTaxonomy
    | ContactTaxonomy
    | PropertyTaxonomy
    | AgentsTaxonomy;
export type ListingsTaxonomy = BaseTaxonomy & OnlyListingsTaxonomy;
export type ListingTaxonomy = BaseTaxonomy & OnlyListingTaxonomy;
export type ContactTaxonomy = ListingTaxonomy & OnlyContactTaxonomy;
export type PropertyTaxonomy = BaseTaxonomy &
    OnlyPropertyTaxonomy &
    Record<string, unknown>;
export type AgentsTaxonomy = BaseTaxonomy & OnlyAgentsTaxonomy;

export const EventNames = {
    SEARCH: 'Search',
    SEARCH_RESULT_IMPRESSION: 'Search Result Impression',
    VIEWED_PAGE: 'Viewed Page',
    LEAD_SENT: 'Lead Sent',
    CLICK_SEARCH_RESULT: 'Search Result Click',
    CLICK_SAVE_SEARCH: 'Click Save Search',
    CLICK_SAVE_SEARCH_SUBMITTED: 'Click Save Search Submitted',
    CLICK_EMAIL_PROPERTY_ALERT: 'Click Email Property Alert',
    CLICK_EMAIL_PROPERTY_ALERT_SUBMITTED:
        'Click Email Property Alert Submitted',
    CLICK_CALL_AGENT: 'Click Call Agent',
    CLICK_EMAIL_AGENT: 'Click Email Agent',
    CLICK_SAVE_LISTING: 'Click Save Listing',
    CLICK_BOOK_AGENT_VALUATION: 'Click Book Agent Valuation',
    DEVELOPMENT_LEAD: 'Development Lead',
    SHARE_CONTENT: 'Share Content',
    EMAIL_OPT_IN: 'Email Marketing Opt-In',
    REGISTRATION: 'Registration',
    SIGN_IN: 'Sign In',
    LISTING_LEAD_SENT: 'Listing Lead Sent',
    PHONE_CLICK_TO_REVEAL: 'Phone Click To Reveal',
    APPLICANT_LEAD_SUBMIT: 'Applicant Lead Submit',
    CLICK_EMAIL_ALERT: 'Click Email Alert',
    TRACK_PROPERTY: 'Track Property Success',
    UNTRACK_PROPERTY: 'Untrack Property',
    CLICK_EDIT_SEARCH: 'Click Edit Search',
    CLICK_ENABLE_EMAIL_ALERT: 'Click Enable Email Alert',
    CLAIM_HOME: 'Claim My Home Success',
    MODULE_SHOWN: 'Module Shown',
    MODULE_INTERACTION: 'Module Interaction',
    CLICK_MY_HOME_BOOK_AGENT_VALUATION: 'Click Valuation CTA',
    CLICK_MY_HOME_STATIC_MAP: 'Click Static Map',
    CLICK_MY_HOME_LOCAL_MARKET_STATS: 'Click Local Market Stats CTA',
    ALERTS_CONSENT_ON: 'Alerts Consent On',
    ALERTS_CONSENT_OFF: 'Alerts Consent Off',
    MODAL_SHOWN: 'Modal Shown',
    MODAL_DISMISSED: 'Modal Dismissed',
    PROMO_BANNER_SHOWN: 'Promo Banner Shown',
    PROMO_BANNER_DISMISSED: 'Promo Banner Dismissed',
    CLICKTHROUGH_PROMO_BANNER: 'Clickthrough Promo Banner',
    CLICK_PROPERTY_TAB: 'Click Property Tab',
    CLICK_RENTAL_TAB: 'Click Rental Tab',
    CLICK_ESTIMATE_INFO: 'Click Estimate Info',
    CLICK_VALUATION_CTA: 'Click Valuation CTA',
    CLICK_ESTIMATE_GRAPH: 'Click Estimate Graph',
    CLICK_NOTIFICATION: 'Notification Click',
    DROPDOWN_SELECT: 'Dropdown Select',
    AGENT_LEAD_SUBMIT: 'Agent Lead Submit',
    FORM_STEP: 'Form Step'
} as const;

type Values<T> = T[keyof T];

export type EventNamesList = Values<typeof EventNames>;

export type IdentifyTrait = {
    user_uuid?: string | null;
};
